import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react';
import { InspectionDialog } from '@/components/crm/InspectionDialog';
import { formatNacelleDisplay } from '@/lib/formatNacelle';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Widget Vue d'ensemble — section LOCATION
 * Liste les nacelles louées dont les inspections (avant / après location)
 * ne sont pas encore faites. Permet de cliquer pour faire l'inspection
 * directement depuis le tableau de bord.
 */
export function SectionInspectionsLocation({ navigate }) {
  const [data, setData] = useState({ manquants: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [allInspections, setAllInspections] = useState([]);          // Inspections récentes (faites)
  const [target, setTarget] = useState(null);                        // {row, type} pour ouvrir le dialog

  const reload = async () => {
    setLoading(true);
    try {
      const [enAttenteRes, recentesRes] = await Promise.all([
        axios.get(`${API_URL}/api/inspections-location/en-attente`),
        // On va chercher les inspections récentes pour montrer celles qui SONT faites
        axios.get(`${API_URL}/api/equipements-inventaire-all-inspections`).catch(() => ({ data: { inspections: [] } })),
      ]);
      setData(enAttenteRes.data || { manquants: [], count: 0 });
      setAllInspections(recentesRes.data?.inspections || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  // Inspections RÉCENTES (faites dans les 30 derniers jours) avec contrat_id
  const recentesAvecContrat = allInspections
    .filter(i => i.contrat_id && ['pre_location', 'post_location'].includes(i.type_inspection))
    .sort((a, b) => (b.date_inspection || '').localeCompare(a.date_inspection || ''))
    .slice(0, 8);

  return (
    <Card className="border-amber-200" data-testid="section-inspections-location">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-amber-600" />
            Inspections de location {data.count > 0 && (
              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full animate-pulse" data-testid="badge-inspections-manquantes">
                {data.count} à faire
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate?.('/inspections-historique')}
              className="text-xs h-7"
              data-testid="btn-historique-inspections"
            >
              📚 Historique
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={reload}
              disabled={loading}
              className="text-xs h-7"
              data-testid="btn-refresh-inspections"
            >
              ↻ Actualiser
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6 text-sm text-slate-400">Chargement…</div>
        ) : data.count === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Toutes les inspections sont à jour 🎉</p>
              <p className="text-xs text-emerald-700">Aucune nacelle louée n'a d'inspection manquante.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {data.manquants.map((row, idx) => (
              <div
                key={`${row.contrat_id}-${row.equipement_id}-${idx}`}
                className="border-2 border-amber-300 bg-amber-50/50 rounded-lg p-3"
                data-testid={`inspection-row-${idx}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-slate-900 truncate">
                      {row.description || 'Nacelle'}
                    </p>
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">Client :</span> {row.client_nom || '—'}
                      {row.numero_soumission && (
                        <button
                          onClick={() => navigate?.(`/crm/soumissions?id=${row.contrat_id}`)}
                          className="ml-2 text-blue-600 hover:underline"
                          data-testid={`link-contrat-${idx}`}
                        >
                          Contrat #{row.numero_soumission}
                        </button>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Période : {row.date_prise || '—'} → {row.date_remise || 'en cours'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-2 shrink-0">
                    {row.manque_pre && (
                      <Button
                        size="sm"
                        onClick={() => setTarget({ row, type: 'pre_location' })}
                        className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8"
                        data-testid={`btn-faire-pre-${idx}`}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        📥 Faire AVANT
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                    {row.manque_post && (
                      <Button
                        size="sm"
                        onClick={() => setTarget({ row, type: 'post_location' })}
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs h-8"
                        data-testid={`btn-faire-post-${idx}`}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        📤 Faire APRÈS
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inspections récemment faites */}
        {recentesAvecContrat.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-emerald-600" />
              Récemment faites
            </p>
            <div className="space-y-1">
              {recentesAvecContrat.map((ins, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] bg-emerald-50/40 rounded px-2 py-1">
                  <span className="truncate min-w-0">
                    {ins.conforme ? '✅' : '⚠️'}{' '}
                    <span className="font-medium">{formatNacelleDisplay({numero_unite: ins.equipement_numero_unite, nom: ins.equipement_nom})}</span>
                    {' — '}
                    <span className="text-slate-600">{ins.client_nom || ''}</span>
                    {' · '}
                    <span className="text-slate-500">{ins.type_inspection === 'pre_location' ? 'AVANT' : ins.type_inspection === 'post_location' ? 'APRÈS' : 'Routine'}</span>
                  </span>
                  <span className="text-slate-400 text-[10px] ml-2 shrink-0">
                    {new Date(ins.date_inspection).toLocaleDateString('fr-CA')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {target && (
        <InspectionDialog
          open={!!target}
          onClose={() => setTarget(null)}
          onSaved={() => { setTarget(null); reload(); }}
          equipementId={target.row.equipement_id}
          equipementNom={target.row.description}
          typeInspection={target.type}
          contratId={target.row.contrat_id}
          clientNom={target.row.client_nom}
        />
      )}
    </Card>
  );
}

export default SectionInspectionsLocation;
